import { memo, useEffect, useState } from "react";

import plusIconDisabled from "../../../../assets/minus-disabled.png";
import minusIcon from "../../../../assets/minus.png";
import plusIcon from "../../../../assets/plus.png";

import { MatchStatus } from "../../../../Utils";
import styles from "./ScorePrediction.module.css";

// Memoize TeamLogo to prevent re-rendering when score changes
const TeamLogo = memo(
	({ team }) => {
		return (
			<div className={styles.logoContainer}>
				<div className={styles.logo}>
					<img
						className={styles.icon}
						src={team?.assets?.logo}
						alt={`${team?.id}-team-logo`}
						// Add loading="eager" to prevent re-loading
						loading="eager"
					/>
				</div>
				<div className={styles.name}>{team?.code}</div>
			</div>
		);
	},
	(prevProps, nextProps) => {
		// Custom comparison - only re-render if team actually changes
		return (
			prevProps.team?.id === nextProps.team?.id &&
			prevProps.team?.assets?.logo === nextProps.team?.assets?.logo &&
			prevProps.team?.code === nextProps.team?.code
		);
	},
);

// Memoize ScorePicker to prevent unnecessary re-renders
const ScorePicker = memo(({ score, onChange, canAddPrediction }) => {
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				justifyContent: "center",
				alignItems: "center",
				border: canAddPrediction ? "1px solid #e5e5e5" : "none",
				borderRadius: "8px",
				width: "50px",
				boxSizing: "border-box",
				height: canAddPrediction ? "100px" : "40px",
			}}
		>
			{canAddPrediction && (
				<div className={styles.buttonContainer} onClick={() => onChange(score === null ? 0 : score + 1)}>
					<img className={styles.button} src={plusIcon} alt={"chevron-up"} />
				</div>
			)}
			<input
				disabled={!canAddPrediction}
				inputMode="numeric"
				className={styles.input}
				style={{
					border: !canAddPrediction && "1px solid rgba(229, 229, 229, 1)",
					borderRadius: !canAddPrediction && "5px",
					background: !canAddPrediction && "none",
				}}
				maxLength={2}
				value={score ?? "-"}
				onChange={(e) => {
					if (!/[0-9, null]/.test(e.nativeEvent.data)) {
						e.preventDefault();
						return;
					}
					const score = e.nativeEvent.data === null ? null : parseInt(e.nativeEvent.data);
					onChange(score);
				}}
			/>
			{canAddPrediction && (
				<div className={styles.buttonContainer} onClick={() => (score > 0 ? onChange(score - 1) : {})}>
					<img className={styles.button} src={score > 0 ? minusIcon : plusIconDisabled} alt={"chevron-down"} />
				</div>
			)}
		</div>
	);
});

const ScorePrediction = ({ match, score, onScoreSet, canMakePredictions }) => {
	const [homeTeamScore, setHomeTeamScore] = useState(null);
	const [awayTeamScore, setAwayTeamScore] = useState(null);

	const canAddPrediction =
		!match.undecided && match.status.type !== MatchStatus.FINISHED && match.status.type !== MatchStatus.IN_PROGRESS && canMakePredictions;

	useEffect(() => {
		setHomeTeamScore(score.homeTeamScore);
		setAwayTeamScore(score.awayTeamScore);
	}, [score]);

	return (
		<div className={styles.container}>
			<div className={styles.predictionContainer}>
				<TeamLogo team={match?.homeTeam} />
				<ScorePicker
					score={homeTeamScore}
					canAddPrediction={canAddPrediction}
					onChange={(newScore) => {
						setHomeTeamScore(newScore);
						if (parseInt(newScore) >= 0 && parseInt(awayTeamScore) >= 0) {
							onScoreSet({ homeTeamScore: newScore, awayTeamScore: awayTeamScore });
						}
					}}
				/>
				<div className={styles.dash}>{"-"}</div>
				<ScorePicker
					score={awayTeamScore}
					canAddPrediction={canAddPrediction}
					onChange={(newScore) => {
						setAwayTeamScore(newScore);
						if (parseInt(newScore) >= 0 && parseInt(homeTeamScore) >= 0) {
							onScoreSet({ homeTeamScore: homeTeamScore, awayTeamScore: newScore });
						}
					}}
				/>
				<TeamLogo team={match?.awayTeam} />
			</div>
		</div>
	);
};

export default ScorePrediction;
